import { Heart, MessageCircle, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { nip57 } from 'nostr-tools';
import type { Notification } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';

interface NotificationItemProps {
  notification: Notification;
  compact?: boolean;
}

/**
 * Extract sats amount from a zap receipt event
 */
function getZapAmount(zapEvent: any): number {
  try {
    // Method 1: amount tag (from zap request, sometimes copied to receipt)
    const amountTag = zapEvent.tags.find(([name]: string[]) => name === 'amount')?.[1];
    if (amountTag) {
      const millisats = parseInt(amountTag);
      return Math.floor(millisats / 1000);
    }

    // Method 2: Extract from bolt11 invoice
    const bolt11Tag = zapEvent.tags.find(([name]: string[]) => name === 'bolt11')?.[1];
    if (bolt11Tag) {
      return nip57.getSatoshisAmountFromBolt11(bolt11Tag);
    }

    // Method 3: Parse from description (zap request JSON)
    const descriptionTag = zapEvent.tags.find(([name]: string[]) => name === 'description')?.[1];
    if (descriptionTag) {
      const zapRequest = JSON.parse(descriptionTag);
      const requestAmountTag = zapRequest.tags?.find(([name]: string[]) => name === 'amount')?.[1];
      if (requestAmountTag) {
        const millisats = parseInt(requestAmountTag);
        return Math.floor(millisats / 1000);
      }
    }
  } catch (err) {
    console.warn('Failed to extract zap amount:', err);
  }

  return 0;
}

/**
 * Truncate text to specified length
 */
function truncateText(text: string, length: number = 60): string {
  if (text.length <= length) return text;
  return text.substring(0, length) + '...';
}

export function NotificationItem({ notification, compact = false }: NotificationItemProps) {
  // Safety check: ensure notification and event exist
  if (!notification || !notification.event || !notification.event.pubkey) {
    console.warn('Invalid notification data:', notification);
    return null;
  }

  const author = useAuthor(notification.event.pubkey);
  const authorName = author.data?.metadata?.name ?? genUserName(notification.event.pubkey);
  const authorImage = author.data?.metadata?.picture;

  // Ensure authorName is always a string for safe rendering
  const safeAuthorName = typeof authorName === 'string' ? authorName : 'Unknown';

  // Generate npub for author profile link
  const authorNpub = nip19.npubEncode(notification.event.pubkey);

  // Get target event title and generate naddr for link
  const targetTitle = notification.targetEvent?.tags?.find?.(([name]) => name === 'title')?.[1];

  // Ensure targetTitle is a string - this is critical for React rendering
  const safeTitleString = typeof targetTitle === 'string' ? targetTitle : 'your post';

  // Generate naddr for target event link
  let targetNaddr: string | null = null;
  if (notification.targetEvent) {
    const dTag = notification.targetEvent.tags.find(([name]) => name === 'd')?.[1] || '';
    targetNaddr = nip19.naddrEncode({
      kind: notification.targetEvent.kind,
      pubkey: notification.targetEvent.pubkey,
      identifier: dTag,
    });
  }

  if (notification.type === 'like') {
    const textColor = notification.isNew ? 'text-white' : 'text-white/50';
    const mutedColor = notification.isNew ? 'text-white/60' : 'text-white/40';

    return (
      <div className={cn(
        'flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.05] transition-colors',
        compact ? 'gap-2' : ''
      )}>
        <Link to={`/${authorNpub}`} className="flex-shrink-0">
          <Avatar className={cn('flex-shrink-0 hover:ring-2 hover:ring-white/20 transition-all', compact ? 'h-7 w-7' : 'h-8 w-8')}>
            <AvatarImage src={authorImage} alt={safeAuthorName} />
            <AvatarFallback className="text-xs">
              {safeAuthorName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0 flex items-center gap-1 flex-wrap">
          <Heart className={cn('text-red-500 fill-red-500 flex-shrink-0', compact ? 'h-3 w-3' : 'h-4 w-4')} />
          <Link to={`/${authorNpub}`} className="hover:underline">
            <p className={cn(`text-sm font-medium ${textColor}`, compact ? 'text-xs' : '')}>
              {truncateText(safeAuthorName, 25)}
            </p>
          </Link>
          <span className={cn(`${mutedColor}`, compact ? 'text-xs' : 'text-sm')}>liked</span>
          {targetNaddr ? (
            <Link to={`/${targetNaddr}`} className="hover:underline">
              <span className={cn(`text-sm font-medium ${textColor}`, compact ? 'text-xs' : '')}>
                {truncateText(safeTitleString, 40)}
              </span>
            </Link>
          ) : (
            <span className={cn(`text-sm font-medium ${textColor}`, compact ? 'text-xs' : '')}>
              {truncateText(safeTitleString, 40)}
            </span>
          )}
        </div>
      </div>
    );
  }

  if (notification.type === 'comment') {
    const textColor = notification.isNew ? 'text-white' : 'text-white/50';
    const mutedColor = notification.isNew ? 'text-white/60' : 'text-white/40';
    const contentColor = notification.isNew ? 'text-white/70' : 'text-white/40';

    return (
      <div className={cn(
        'flex gap-3 p-2 rounded-lg hover:bg-white/[0.05] transition-colors',
        compact ? 'gap-2' : ''
      )}>
        <Link to={`/${authorNpub}`} className="flex-shrink-0">
          <Avatar className={cn('flex-shrink-0 hover:ring-2 hover:ring-white/20 transition-all', compact ? 'h-7 w-7' : 'h-8 w-8')}>
            <AvatarImage src={authorImage} alt={safeAuthorName} />
            <AvatarFallback className="text-xs">
              {safeAuthorName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <MessageCircle className={cn('text-blue-500 flex-shrink-0', compact ? 'h-3 w-3' : 'h-4 w-4')} />
            <Link to={`/${authorNpub}`} className="hover:underline">
              <p className={cn(`text-sm font-medium ${textColor}`, compact ? 'text-xs' : '')}>
                {truncateText(safeAuthorName, 25)}
              </p>
            </Link>
            <span className={cn(`${mutedColor}`, compact ? 'text-xs' : 'text-sm')}>commented on</span>
            {targetNaddr ? (
              <Link to={`/${targetNaddr}`} className="hover:underline">
                <p className={cn(`text-sm font-medium ${textColor}`, compact ? 'text-xs' : '')}>
                  {truncateText(safeTitleString, 30)}
                </p>
              </Link>
            ) : (
              <p className={cn(`text-sm font-medium ${textColor}`, compact ? 'text-xs' : '')}>
                {truncateText(safeTitleString, 30)}
              </p>
            )}
          </div>
          <p className={cn(`${contentColor} truncate mt-0.5`, compact ? 'text-xs' : 'text-sm')}>
            {truncateText(notification.event.content, compact ? 50 : 80)}
          </p>
        </div>
      </div>
    );
  }

  if (notification.type === 'zap') {
    const amount = getZapAmount(notification.event);
    const textColor = notification.isNew ? 'text-white' : 'text-white/50';
    const mutedColor = notification.isNew ? 'text-white/60' : 'text-white/40';

    return (
      <div className={cn(
        'flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.05] transition-colors',
        compact ? 'gap-2' : ''
      )}>
        <Link to={`/${authorNpub}`} className="flex-shrink-0">
          <Avatar className={cn('flex-shrink-0 hover:ring-2 hover:ring-white/20 transition-all', compact ? 'h-7 w-7' : 'h-8 w-8')}>
            <AvatarImage src={authorImage} alt={safeAuthorName} />
            <AvatarFallback className="text-xs">
              {safeAuthorName.substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className={cn('bg-orange-500/20 text-orange-300 border-0 flex-shrink-0', compact ? 'px-1.5 py-0 text-xs' : '')}>
            <Zap className={cn('flex-shrink-0', compact ? 'h-3 w-3 mr-0.5' : 'h-3.5 w-3.5 mr-1')} />
            {amount}
          </Badge>
          <span className={cn(`${mutedColor}`, compact ? 'text-xs' : 'text-sm')}>from</span>
          <Link to={`/${authorNpub}`} className="hover:underline">
            <span className={cn(`font-medium ${textColor}`, compact ? 'text-xs' : 'text-sm')}>
              {truncateText(safeAuthorName, 25)}
            </span>
          </Link>
          <span className={cn(`${mutedColor}`, compact ? 'text-xs' : 'text-sm')}>on</span>
          {targetNaddr ? (
            <Link to={`/${targetNaddr}`} className="hover:underline">
              <span className={cn(`font-medium ${textColor}`, compact ? 'text-xs' : 'text-sm')}>
                {truncateText(safeTitleString, 30)}
              </span>
            </Link>
          ) : (
            <span className={cn(`font-medium ${textColor}`, compact ? 'text-xs' : 'text-sm')}>
              {truncateText(safeTitleString, 30)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return null;
}
